import React, { useState, useEffect, useRef, ReactNode } from 'react';
import eventsService from '../services/events';
import TagMultiSelect from './TagMultiSelect';
import { ITag } from '../types/tags';

interface EventInviteButtonProps {
  event: any;
}

const EventInviteButton: React.FC<EventInviteButtonProps> = ({ event }) => {
  const [open, setOpen] = useState(false);
  const [inviteType, setInviteType] = useState('all');
  const [selectedTags, setSelectedTags] = useState<ITag[]>([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [templateId, setTemplateId] = useState('event-invitation');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewResults, setPreviewResults] = useState<any>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      // Skip if the click is inside the modal or inside any MUI Autocomplete dropdown
      const target = e.target as HTMLElement;
      const isAutocompleteClick = !!target.closest('.MuiAutocomplete-popper');

      if (modalRef.current && !modalRef.current.contains(target) && !isAutocompleteClick && open) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const resetForm = () => {
    setInviteType('all');
    setSelectedTags([]);
    setSubject('');
    setMessage('');
    setPreviewResults(null);
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const validateForm = () => {
    // Validate that tags are selected when inviteType is byTag
    if (inviteType === 'byTag' && (!selectedTags || selectedTags.length === 0)) {
      setError('Please select at least one tag');
      return false;
    }
    return true;
  };

  const handlePreview = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const results = await eventsService.invite(event.id, {
        tagIds: inviteType === 'byTag' ? selectedTags.map((tag: ITag) => tag._id) : null,
        subject,
        message,
        templateId,
        preview: true
      });
      setPreviewResults(results);
    } catch (err: any) {
      console.log(err);
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to preview invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitations = async () => {
    setLoading(true);
    setError(null);

    try {
      await eventsService.invite(event.id, {
        tagIds: inviteType === 'byTag' ? selectedTags.map((tag: ITag) => tag._id) : null,
        subject,
        message,
        templateId,
        preview: false
      });
      handleClose();
    } catch (err: any) {
      console.log(err);
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to send invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteTypeChange = (value: string) => {
    setInviteType(value);
    if (value !== 'byTag') {
      setSelectedTags([]); // Reset tags when switching away from byTag
    }
  };

  const handleBackToForm = () => {
    setPreviewResults(null);
  };

  return (
    <>
      <button
        className={`px-3 py-1.5 md:px-4 md:py-2 bg-purple-600 text-white font-medium text-sm md:text-base rounded shadow hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={() => setOpen(true)}
        disabled={loading}
        aria-label="Send Invitations"
      >
        {isMobile ? 'Invite' : 'Send Invitations'}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[1201] flex items-center justify-center p-2 sm:p-4">
          <div
            ref={modalRef}
            className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
          >
            {!previewResults ? (
              <form onSubmit={handlePreview} className="flex flex-col h-full overflow-hidden">
                <div className="p-3 sm:p-6 overflow-y-auto flex-grow">
                  <div className="flex justify-between items-center mb-3 sm:mb-4">
                    <h2 className="text-lg sm:text-xl font-semibold">Send Event Invitations</h2>
                    <button
                      type="button"
                      aria-label="close"
                      onClick={handleClose}
                      className="p-1 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    >
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {error && (
                    <div className="mb-3 sm:mb-4 p-2 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
                      {error}
                    </div>
                  )}

                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Invitation Type
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            value="all"
                            checked={inviteType === 'all'}
                            onChange={(e) => handleInviteTypeChange(e.target.value)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="text-sm sm:text-base text-gray-900">All Members</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            value="byTag"
                            checked={inviteType === 'byTag'}
                            onChange={(e) => handleInviteTypeChange(e.target.value)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="text-sm sm:text-base text-gray-900">By Tag</span>
                        </label>
                      </div>
                    </div>

                    {inviteType === 'byTag' && (
                      <div className="mt-2 sm:mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                          Tags <span className="text-red-500">*</span>
                        </label>
                        <TagMultiSelect
                          selectedTags={selectedTags}
                          setSelectedTags={setSelectedTags}
                          type={["member"]}
                        />
                        {error && error.includes('tag') && (
                          <p className="mt-1 text-xs sm:text-sm text-red-600">{error}</p>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        required
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Message
                      </label>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                        rows={isMobile ? 3 : 4}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Template
                      </label>
                      <select
                        value={templateId}
                        onChange={(e) => setTemplateId(e.target.value)}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="event-invitation">Event Invitation</option>
                        <option value="event-announcement">Event Announcement</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 rounded-b-lg flex flex-wrap sm:flex-nowrap justify-end gap-2 sm:space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={loading}
                    className="w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full sm:w-auto min-w-[80px] sm:min-w-[100px] px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${loading ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                  >
                    {loading ? (
                      <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                    ) : (
                      'Preview'
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex flex-col h-full overflow-hidden">
                <div className="p-3 sm:p-6 overflow-y-auto flex-grow">
                  <div className="flex justify-between items-center mb-3 sm:mb-4">
                    <h2 className="text-lg sm:text-xl font-semibold">Preview Results</h2>
                    <button
                      type="button"
                      aria-label="close"
                      onClick={handleClose}
                      className="p-1 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    >
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {error && (
                    <div className="mb-3 sm:mb-4 p-2 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
                      {error}
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="text-md font-medium mb-2">Email will be sent to: {previewResults.to} members</h3>
                    <div className="bg-gray-50 p-3 rounded border overflow-auto max-h-60">
                      {previewResults.to > 0
                        ? (
                          <div className="text-sm">
                            <p><span className="font-medium">{previewResults.to}</span> recipient(s)</p>
                            {previewResults.recipients && (
                              <ul className="list-disc list-inside mt-1">
                                {previewResults.recipients.map((recipient: any, index: number) => (
                                  <li key={index} className="truncate">
                                    {recipient.email} {recipient.name ? `(${recipient.name})` : ''}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )
                        : (
                          <p>No members found</p>
                        )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h3 className="text-md font-medium mb-2">Email Content:</h3>
                    <div className="bg-gray-50 p-3 rounded border overflow-auto max-h-60">
                      <div className="text-sm">
                        <p><span className="font-medium">From:</span> {previewResults.from.name} ({previewResults.from.email})</p>
                        <p className="mt-2"><span className="font-medium">Subject:</span> {previewResults.subject}</p>
                        <div className="mt-2">
                          <span className="font-medium">Message:</span>
                          <div className="mt-1 whitespace-pre-wrap">{previewResults.message}</div>
                        </div>
                        <p className="mt-2"><span className="font-medium">Template:</span> {previewResults.templateId}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 rounded-b-lg flex flex-wrap sm:flex-nowrap justify-end gap-2 sm:space-x-3">
                  <button
                    type="button"
                    onClick={handleBackToForm}
                    disabled={loading}
                    className="w-full sm:w-auto px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSendInvitations}
                    disabled={loading}
                    className={`w-full sm:w-auto min-w-[80px] sm:min-w-[100px] px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${loading ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                  >
                    {loading ? (
                      <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                    ) : (
                      'Send Invitations'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default EventInviteButton; 